import {useMemo} from 'react';

import {type Line, type Point} from '../../util/plotHelpers/types';

/**
 * Configuration for line filtering based on chart state.
 */
export type LineFilterConfig = {
  /** Scale type for the x-axis - affects which points are filtered */
  xScale: 'linear' | 'log';
  /** Scale type for the y-axis - affects which points are filtered */
  yScale: 'linear' | 'log';
  /** Whether this is the full-fidelity (BRSDQ) rendering path */
  isFullFidelity: boolean;
  /** Whether any chart element is currently being hovered */
  isHovered: boolean;
};

/**
 * Return type for useLinesForDisplay hook.
 * Will be expanded to include legendLines, hiddenLines, etc.
 */
export type LinesForDisplayResult = {
  /** Lines after scale filtering and baseline styling applied */
  filteredLines: Line[];
};

// ============================================================================
// PURE TRANSFORMATION FUNCTIONS
// ============================================================================

/**
 * Pure function to filter line points based on scale type.
 * Returns same reference when no filtering needed (optimization).
 *
 * Combines x-axis and y-axis filtering in a single pass for efficiency.
 *
 * Filtering rules:
 * - x log scale: filter x <= 0 and non-finite x
 * - y log scale: filter y <= 0
 * - Sampled path: also filter non-finite x and y
 * - Full fidelity + both linear: no filtering (backend handles it)
 */
export function filterPointsByScale(
  lines: Line[],
  xScale: 'linear' | 'log',
  yScale: 'linear' | 'log',
  isFullFidelity: boolean
): Line[] {
  // Fast path: full fidelity with both linear scales needs no filtering
  if (isFullFidelity && xScale === 'linear' && yScale === 'linear') {
    return lines;
  }

  // Build predicate based on scale configuration
  const shouldKeep = (p: Point): boolean => {
    // x-axis checks
    if (xScale === 'log') {
      if (!Number.isFinite(p.x) || p.x <= 0) {
        return false;
      }
    } else if (!isFullFidelity && !Number.isFinite(p.x)) {
      return false;
    }

    // y-axis checks
    if (yScale === 'log') {
      if (p.y <= 0) {
        return false;
      }
    } else if (!isFullFidelity && !Number.isFinite(p.y)) {
      return false;
    }

    return true;
  };

  let filteredLines: Line[] | undefined;
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const firstRemoved = line.data.findIndex(point => !shouldKeep(point));
    if (firstRemoved === -1) {
      continue;
    }

    // Copy only after a rejected point; valid lines keep their existing arrays.
    const filtered = line.data.slice(0, firstRemoved);
    for (let j = firstRemoved + 1; j < line.data.length; j++) {
      if (shouldKeep(line.data[j])) {
        filtered.push(line.data[j]);
      }
    }
    filteredLines ??= lines.slice();
    filteredLines[i] = {...line, data: filtered};
  }
  return filteredLines ?? lines;
}

/**
 * Pure function to apply baseline line styling based on hover state.
 * Returns new line objects for baselines, unchanged references for others.
 *
 * - When NOT hovered: baseline runs show as solid (default)
 * - When hovered: baseline runs show as dashed (distinguishes from hovered run)
 *
 * @param lines - Lines to process
 * @param isHovered - Whether the chart is being hovered
 * @returns Lines with baseline styling applied (new objects for modified lines)
 */
export function applyBaselineHoverStyles(
  lines: Line[],
  isHovered: boolean
): Line[] {
  // Fast path: no baselines means no changes needed.
  // Returns same reference so React can skip downstream re-renders.
  const hasBaselines = lines.some(line => line.run?.isBaseline);
  if (!hasBaselines) {
    return lines;
  }

  return lines.map(line => {
    if (line.run?.isBaseline) {
      return {
        ...line,
        mark: isHovered ? 'dashed' : 'solid',
      };
    }
    return line;
  });
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Custom hook that prepares lines for display by applying scale-based filtering
 * and baseline styling.
 *
 * Uses a two-stage memoization strategy:
 * 1. **Static stage**: Scale filtering, only re-runs when lines/scale change
 * 2. **Dynamic stage**: Baseline styling, re-runs when hover state changes
 *
 * This separation optimizes for the common case where hover events are frequent
 * but line data changes are rare.
 *
 * All transformations are pure (no mutation of input data).
 *
 * @param lines - Raw lines from RSDQ or BRSDQ data path
 * @param config - Configuration for filtering behavior
 * @returns Object containing filtered line collections
 *
 * @example
 * ```tsx
 * const { filteredLines } = useLinesForDisplay(lines, {
 *   xScale: 'linear',
 *   isFullFidelity: true,
 *   isHovered: false,
 * });
 * ```
 */
export function useLinesForDisplay(
  lines: Line[],
  config: LineFilterConfig
): LinesForDisplayResult {
  const {xScale, yScale, isFullFidelity, isHovered} = config;

  // STAGE 1: Static filtering (expensive)
  // Only re-runs when lines or scale configuration changes
  const linesWithValidPoints = useMemo(
    () => filterPointsByScale(lines, xScale, yScale, isFullFidelity),
    [lines, xScale, yScale, isFullFidelity]
  );

  // STAGE 2: Dynamic styling (cheap)
  // Re-runs when hover state changes, but only processes baseline lines
  const filteredLines = useMemo(
    () => applyBaselineHoverStyles(linesWithValidPoints, isHovered),
    [linesWithValidPoints, isHovered]
  );

  return {filteredLines};
}
